import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs/esm/index';
import { Contests } from 'apps/account/src/account-v2/src/app/features/advocacy/group/audiences/constants/index';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { FtDateRangePickerComponent } from '../contest-sheets/ft-date-range-picker.component';

/**
 * Native port of the AngularJS create-contest modal, opened for "New contest" from the
 * audiences + contests pages. Renders the <ft-modal> shell via ft-modal-overlay +
 * ft-modal-chrome-ng. sourceContests is computed once per instance (initOnce); menuContests
 * is re-filtered by theme on each open().
 *
 * Deliberate divergence: on cancel/navigate-away rollback the picked date fields are kept
 * (not rolled back) — v1's snapshot rollback corrupts moment objects; the string/null
 * fields (name, sourceContestName, sourceContestUUID) roll back exactly as v1.
 */
@Component({
    selector: 'ft-create-contest-modal-ng',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        FtModalComponent,
        FtModalChromeComponent,
        FtDateRangePickerComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-create-contest-modal.component.html'
})
export class FtCreateContestModalComponent implements OnDestroy {
    /** v1 bindings (createContestModal.component.ts OPTIONS) — re-set by the launcher on EVERY show(). */
    readonly propertyUuid = input<any>();
    readonly clonableContests = input<any[]>([]);
    /** v1 `on-created="onCreated(contestUuid)"` — called with the raw uuid. */
    readonly onCreated = input<(contestUuid: string) => void>(() => {});
    readonly onCreateFailed = input<() => void>(() => {});
    readonly theme = input<any>();

    /** v1 'modal-*.done'/'modal-*.cancel' broadcasts — the launcher destroys this instance on either. */
    readonly closed = output<'done' | 'cancel'>();

    private readonly createForm = viewChild(NgForm);

    protected readonly isOpen = signal(false);
    /** v1 controller field — initialized ONCE per instance (dayjs, like v1). */
    protected readonly contest = signal<any>({
        startDate: dayjs(),
        endDate: dayjs().add(3, 'month'),
        name: '',
        sourceContestName: null,
        sourceContestUUID: null,
    });
    /** v1 $onInit product — computed once, never refreshed (see the class doc). */
    private sourceContests: any[] = [];
    /** The v1 per-digest `| filter:{theme}` result — recomputed per open() (theme can change on reuse). */
    protected readonly menuContests = signal<any[]>([]);
    protected readonly dropdownActive = signal(false);

    private initialized = false;
    private snapshotState: any = null;

    private readonly photoContest = inject(PhotoContestApiService);

    /** The launcher's per-show entry (v1 ftModal.show → the 'modal-*.show' handler). */
    open(): void {
        this.initOnce();
        this.menuContests.set(this.filterByTheme(this.sourceContests, this.theme()));
        // v1 'modal-*.show': snapshot.retake() (modal.js:346).
        this.snapshotState = { ...this.contest() };
        // ⭐ ngStrap re-linked a fresh clone per show → pristine form AND a closed
        // dropdown on EVERY show (the 2v lesson); the native instances persist,
        // so reset both.
        this.resetFormState();
        this.dropdownActive.set(false);
        this.isOpen.set(true);
        document.body.classList.add('modal-open', 'modal-with-am-fade');
        this.focusFirstInput();
    }

    /** v1 ftModal `$locationChangeStart` → instance.hide(): hide WITHOUT the cancel
     *  broadcast — rollback, keep the instance alive (NO closed emit). */
    hideForNavigation(): void {
        if (this.isOpen()) this.hide(false);
    }

    /** The chrome Done — v1 modalScope.done() (modal.js:276-303). */
    protected done(): void {
        // onDone = vm.create: fires the POST and returns undefined → immediate
        // hide + 'done' broadcast → service destroy, POST still in flight.
        this.create();
        this.hide(true);
        this.closed.emit('done');
    }

    /** The chrome X — v1 modalScope.cancel() (modal.js:305-314). */
    protected cancel(): void {
        this.hide(false);
        this.closed.emit('cancel');
    }

    /** Verbatim v1 create (createContestModal.component.ts:39-71) — except the
     *  name is edge-trimmed AT USE: AngularJS NgModel trims by default, so the
     *  v1 model could never carry leading/trailing whitespace into the POST
     *  (the ⭐ 2v ng-trim lesson — an NgModel trim-parser would clobber typing,
     *  so trim where the value is consumed instead; the Save gate likewise
     *  treats whitespace-only as empty via the chrome doneDisabled binding). */
    create = () => {
        const name = (this.contest().name ?? '').trim();
        const newContestPromise = this.contest().sourceContestUUID
            ? this.photoContest.clone(this.propertyUuid(), {
                  startDate: this.contest().startDate.format('YYYY-MM-DD'),
                  endDate: this.contest().endDate.format('YYYY-MM-DD'),
                  newName: name,
                  oldContestUUID: this.contest().sourceContestUUID,
                  theme: this.theme(),
              })
            : this.photoContest.create(this.propertyUuid(), {
                  startDate: this.contest().startDate.format('YYYY-MM-DD'),
                  endDate: this.contest().endDate.format('YYYY-MM-DD'),
                  theme: this.theme(),
                  isPrivate: this.theme() === Contests.team && true,
                  languages: [
                      {
                          languageCode: 'en',
                          name: name,
                          guestMessage: '',
                          followerMessage: '',
                      },
                  ],
              });
        newContestPromise.then(
            (contestUuid) => {
                this.onCreated()(contestUuid);
            },
            () => {
                this.onCreateFailed()();
            },
        );
    };

    /** Verbatim v1 selectSourceContest. */
    protected selectSourceContest = (contest: any) => {
        this.contest().sourceContestName = contest.name;
        this.contest().sourceContestUUID = contest.UUID;
    };

    /** v1 `ft-click-class toggle-class="active"` — toggle on inside click (an item
     *  click bubbles here too, closing the menu like v1), close on outside click. */
    protected toggleDropdown(event: Event): void {
        event.stopPropagation();
        this.dropdownActive.set(!this.dropdownActive());
    }

    // The document handler closes on outside clicks only — inside clicks
    // stopPropagation in toggleDropdown (the ft-incentive-selector-ng recipe).
    private readonly documentClick = () => {
        if (this.dropdownActive()) {
            this.dropdownActive.set(false);
        }
    };

    constructor() {
        document.addEventListener('click', this.documentClick);
    }

    ngOnDestroy(): void {
        document.removeEventListener('click', this.documentClick);
        // The launcher destroys on done/cancel (body classes already removed by
        // hide()); belt-and-braces for a destroy while open.
        if (this.isOpen()) {
            document.body.classList.remove('modal-open', 'modal-with-am-fade');
        }
    }

    /** v1 $onInit — ran once per instance, AFTER show() had seeded the scope props. */
    private initOnce(): void {
        if (this.initialized) return;
        this.initialized = true;
        const emptyContest = { UUID: null, name: 'Do not copy' };
        this.sourceContests = this.clonableContests().slice();
        this.sourceContests.unshift(emptyContest);
        this.selectSourceContest(emptyContest);
    }

    private hide(donePressed: boolean): void {
        this.isOpen.set(false);
        // ngStrap's post-hide body-class removal is RACY in v1 itself (the am-fade
        // removal rides an animation callback the cold first open can leak past —
        // the two-host diag observed BOTH residues on the frozen baseline at the
        // same timing). Match the terminal v1 state: both removed.
        document.body.classList.remove('modal-open', 'modal-with-am-fade');
        if (!donePressed && this.snapshotState) {
            // v1 'modal-*.hide' with !donePressed → snapshot.rollback (modal.js:354-361)
            // — EXCEPT the dates: v1's rollback GUTS picked moment objects (see the
            // class doc's deliberate-divergence note), so they are kept as-is.
            this.contest.set({
                ...this.snapshotState,
                startDate: this.contest().startDate,
                endDate: this.contest().endDate,
            });
        }
    }

    /**
     * The AngularJS `filter: { theme: vm.theme }` object-expression filter for
     * the primitive-valued case (filterFilter's default comparator): an
     * undefined EXPECTED value skips the predicate entirely (all items pass —
     * the contests page passes no theme); otherwise an item without the
     * property fails, null compares by identity, and both sides stringify +
     * lowercase + substring-match (themes are 0/1/2 → effectively equality).
     */
    private filterByTheme(items: any[], theme: any): any[] {
        if (theme === undefined) return items.slice();
        return items.filter((item) => {
            const actual = item?.theme;
            if (actual === undefined) return false;
            if (actual === null || theme === null) return actual === theme;
            return ('' + actual).toLowerCase().indexOf(('' + theme).toLowerCase()) !== -1;
        });
    }

    /** v1 ftModal focusFirstInput on every show (modal.js:345-352) — the name input. */
    private focusFirstInput(): void {
        setTimeout(() => {
            const el = document.querySelector(
                '#AccountV2\\.Components\\.CreateContestModal input, #AccountV2\\.Components\\.CreateContestModal textarea',
            ) as HTMLElement | null;
            el?.focus();
        });
    }

    private resetFormState(): void {
        this.createForm()?.form.markAsPristine();
        this.createForm()?.form.markAsUntouched();
    }
}
